'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import Link from 'next/link';

import ConsultantCardHeader from '@/components/sections/DoctorCard/ConsultantCardHeader';
import { ConsultantDoctifyMiniWidget } from '@/components/sections/DoctorCard/ConsultantDoctifyMiniWidget';
import type { DoctorCard } from '@/types/doctor';

interface DoctorsSliderProps {
  doctors: DoctorCard[];
}

const DoctorsSlider = ({ doctors }: DoctorsSliderProps) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const updateScrollState = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const { scrollLeft, scrollWidth, clientWidth } = scroller;
    const buffer = 5;

    const scrollable = scrollWidth > clientWidth + buffer;
    setIsScrollable(scrollable);
    setCanScrollLeft(scrollLeft > buffer);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - buffer);

    const pages = Math.max(1, Math.ceil(scrollWidth / clientWidth));
    setTotalPages(pages);
    setCurrentPage(Math.round(scrollLeft / clientWidth));
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    updateScrollState();

    const resizeObserver = new ResizeObserver(() => updateScrollState());
    resizeObserver.observe(scroller);

    return () => resizeObserver.disconnect();
  }, [doctors, updateScrollState]);

  const handleScrollSide = useCallback((direction: 'left' | 'right') => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const scrollAmount =
      direction === 'left'
        ? -scroller.clientWidth * 0.8
        : scroller.clientWidth * 0.8;

    scroller.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }, []);

  const handleDotClick = useCallback((pageIndex: number) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollTo({
      left: pageIndex * scroller.clientWidth,
      behavior: 'smooth',
    });
  }, []);

  return (
    <div className="w-full">
      <div
        ref={scrollerRef}
        onScroll={updateScrollState}
        className="flex snap-x snap-mandatory gap-2 overflow-x-auto px-1 pb-2"
      >
        {doctors.map((doctor) => {
          const spec = doctor.primarySpeciality || doctor.specialities?.[0];

          return (
            <Link
              key={doctor.slug}
              href={`/our-specialists/${doctor.slug}`}
              className="flex w-56 shrink-0 snap-start flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm sm:w-60"
            >
              <div className="shrink-0">
                <ConsultantCardHeader
                  slug={doctor.slug}
                  imageUrl={doctor.image?.url}
                  name={doctor?.fullName ?? 'Doctor'}
                  compact
                />
              </div>

              <div className="flex min-w-0 flex-col gap-1 p-3">
                <span className="text-text-ink truncate text-sm leading-tight font-semibold">
                  {doctor.fullName}
                </span>

                {spec && (
                  <span className="text-theme-brand-blue border-theme-success-2 bg-theme-success-opac self-start truncate rounded-full border px-2 py-0.5 text-[11px] font-bold">
                    {spec}
                  </span>
                )}

                {doctor.doctifySlug && (
                  <div className="mt-1">
                    <ConsultantDoctifyMiniWidget
                      doctifySlug={doctor.doctifySlug}
                      widgetId={`home-doctify-${doctor.slug}`}
                      justify="justify-start"
                    />
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </div>

      {isScrollable && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => handleScrollSide('left')}
            disabled={!canScrollLeft}
            aria-label="Previous"
            className="border-gray-150 flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border bg-white text-slate-700 shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-all duration-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <svg
              className="text-text-main h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5L8.25 12l7.5-7.5"
              />
            </svg>
          </button>

          <div className="flex items-center gap-1.5">
            {Array.from({ length: totalPages }).map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleDotClick(idx)}
                aria-label={`Go to page ${idx + 1}`}
                className={`rounded-full transition-all duration-300 ${
                  idx === currentPage
                    ? 'bg-theme-brand-blue h-2 w-4'
                    : 'h-2 w-2 bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={() => handleScrollSide('right')}
            disabled={!canScrollRight}
            aria-label="Next"
            className="border-gray-150 flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border bg-white text-slate-700 shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-all duration-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <svg
              className="text-text-main h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.25 4.5l7.5 7.5-7.5 7.5"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default DoctorsSlider;
